/**
 * Coding Assistant Store
 *
 * Lightweight Zustand store that tracks UI-only state for the coding assistant.
 *
 * Currently tracks: which appointments/encounters have pending ambiguous E&M
 * variants that the physician has not yet resolved. This lets the sign-note
 * flow block signing until the physician picks a patient type.
 *
 * Key is the resourceId (`appointmentId` or `encounterId`). Values are booleans.
 */

import { create } from 'zustand';

interface CodingAssistantStore {
  /**
   * Map of resourceId -> `true` when the E&M pipeline returned ambiguous
   * variants and the physician has not yet selected one. Absent (or `false`)
   * means no pending selection.
   */
  emVariantsPending: Record<string, boolean>;

  /**
   * Mark a resource as having pending E&M variants (ambiguous selection).
   * Use `false` to clear the flag (variant selected or pipeline regenerated).
   */
  setEmVariantsPending: (resourceId: string, pending: boolean) => void;

  /**
   * Convenience getter: is this resource currently blocked from signing
   * due to pending E&M variants?
   */
  isEmVariantsPending: (resourceId: string | undefined | null) => boolean;
}

export const useCodingAssistantStore = create<CodingAssistantStore>((set, get) => ({
  emVariantsPending: {},

  setEmVariantsPending: (resourceId, pending) => {
    if (!resourceId) return;
    set((state) => {
      const next = { ...state.emVariantsPending };
      if (pending) {
        next[resourceId] = true;
      } else {
        delete next[resourceId];
      }
      return { emVariantsPending: next };
    });
  },

  isEmVariantsPending: (resourceId) => {
    if (!resourceId) return false;
    return Boolean(get().emVariantsPending[resourceId]);
  },
}));
